import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartOptions } from 'ag-charts-community';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { take } from 'rxjs';
import { AccountingReportsService } from '../accounting-reports.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { BalanceSheetPdfGenerator } from './balance-sheet-pdf';

@Component({
  selector: 'app-balance-sheet',
  standalone: true,
  imports: [FormsModule, BsDatepickerModule, CurrencyPipe, DecimalPipe, AgChartsModule, CollapseModule],
  templateUrl: './balance-sheet.component.html',
  styleUrl: './balance-sheet.component.css',
})
export class BalanceSheetComponent implements OnInit {
  public chartOptions!: AgChartOptions;
  public assetChartOptions!: AgChartOptions;
  public gaugeChartOptions!: AgChartOptions;
  public currentRatio: number = 0;
  public asOfDate: Date = new Date();
  public asOfDateInput: any = new Date();
  public bsConfig: Partial<BsDatepickerConfig>;
  public isChartCollapsed: boolean = false;
  public isAssetsCollapsed: boolean = true;
  public isLiabilitiesCollapsed: boolean = true;
  public period: string = 'Today';
  public periods: string[] = [
    'Today',
    'Last Month',
    'Last Quarter',
    'Last Year',
    'Custom',
  ];
  public totalAssets: number = 0;
  public totalLiabilities: number = 0;
  public totalEquity: number = 0;

  // Mock data for table breakdown
  public assetAccounts = [
    {
      category: 'Current Assets',
      items: [
        { name: 'Checking Account', amount: 45000 },
        { name: 'Savings Account', amount: 20000 },
        { name: 'Accounts Receivable', amount: 15000 },
        { name: 'Inventory Asset', amount: 110000 },
      ],
    },
    {
      category: 'Fixed Assets',
      items: [
        { name: 'Store Fixtures & Equipment', amount: 35000 },
        { name: 'Computers & Software', amount: 10000 },
        { name: 'Accumulated Depreciation', amount: 10000 },
      ],
    },
  ];

  public liabilityAccounts = [
    {
      category: 'Current Liabilities',
      items: [
        { name: 'Accounts Payable', amount: 12000 },
        { name: 'Credit Card', amount: 4500 },
        { name: 'Sales Tax Payable', amount: 8500 },
      ],
    },
    {
      category: 'Long-Term Liabilities',
      items: [{ name: 'Business Loan', amount: 70000 }],
    },
  ];

  public equityAccounts = [
    { name: "Owner's Investment", amount: 100000 },
    { name: 'Retained Earnings', amount: 50000 },
  ];

  constructor(
    private reportsService: AccountingReportsService,
    private sharedService: SharedService
  ) {
    this.bsConfig = Object.assign(
      {},
      {
        containerClass: 'theme-dark-blue',
        dateInputFormat: 'MM/DD/YYYY',
        showWeekNumbers: false,
        isAnimated: true,
        adaptivePosition: true,
      }
    );
  }

  ngOnInit() {
    if (this.sharedService.company_info_loaded) {
      this.setPeriod('Today');
      return;
    }

    this.sharedService.company_info_retrieved$.pipe(take(1)).subscribe(() => {
      this.setPeriod('Today');
    });
  }

  setPeriod(period: string) {
    this.period = period;
    const now = new Date();
    let asOf = new Date();

    if (period === 'Today') {
      asOf = now;
    } else if (period === 'Last Month') {
      asOf = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (period === 'Last Quarter') {
      const quarter = Math.floor(now.getMonth() / 3);
      asOf = new Date(now.getFullYear(), quarter * 3, 0);
    } else if (period === 'Last Year') {
      asOf = new Date(now.getFullYear(), 0, 0);
    }

    if (period !== 'Custom') {
      this.asOfDateInput = asOf;
      this.onDateChange();
    }
  }

  exportPdf() {
    const generator = new BalanceSheetPdfGenerator();
    const companyInfo = this.sharedService.company_info;
    const data = {
      totalAssets: this.totalAssets,
      totalLiabilities: this.totalLiabilities,
      totalEquity: this.totalEquity,
      assetAccounts: this.assetAccounts,
      liabilityAccounts: this.liabilityAccounts,
      equityAccounts: this.equityAccounts
    };
    const doc = generator.generatePdf(data, companyInfo, this.asOfDate);
    doc.save(`Balance_Sheet_${this.asOfDate.toLocaleDateString().replace(/\//g, '-')}.pdf`);
  }

  onDateChange() {
    this.asOfDate = new Date(this.asOfDateInput);
    this.loadData();
  }

  loadData() {
    this.reportsService.getBalanceSheet(this.asOfDateInput).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const d = res.data;

          this.assetAccounts = d.assets
            ? [
                { category: 'Current Assets', items: d.assets.current || [] },
                { category: 'Fixed Assets', items: d.assets.fixed || [] },
              ]
            : [];

          this.liabilityAccounts = d.liabilities
            ? [
                {
                  category: 'Current Liabilities',
                  items: d.liabilities.current || [],
                },
                {
                  category: 'Long-Term Liabilities',
                  items: d.liabilities.longTerm || [],
                },
              ]
            : [];

          this.equityAccounts = d.equity ? d.equity.items || [] : [];

          // Calculate totals
          this.totalAssets = this.calculateGroupTotal(this.assetAccounts);
          this.totalLiabilities = this.calculateGroupTotal(
            this.liabilityAccounts,
          );
          this.totalEquity = this.equityAccounts.reduce(
            (acc: number, item: any) => acc + (item.amount || 0),
            0,
          );

          this.initChart();
        }
      },
      error: (err) => console.error('Error loading Balance Sheet data:', err),
    });
  }

  private calculateGroupTotal(groups: any[]): number {
    let total = 0;
    for (const group of groups) {
      for (const item of group.items) {
        total += item.amount || 0;
      }
    }
    return total;
  }

  private initChart() {
    // 1. Overview Chart
    this.chartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Overview', color: 'white', fontSize: 13 },
      data: [
        { category: 'Assets', amount: this.totalAssets },
        { category: 'Liabilities', amount: this.totalLiabilities },
        { category: 'Equity', amount: this.totalEquity },
      ],
      series: [
        {
          type: 'pie',
          angleKey: 'amount',
          calloutLabelKey: 'category',
          sectorLabelKey: 'amount',
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k',
          },
          fills: ['#00a8ff', '#ff4d4d', '#9c27b0'],
        },
      ],
      legend: { enabled: false },
    } as any;

    // 2. Asset Composition (Donut)
    const assetData: any[] = [];
    this.assetAccounts.forEach((group) => {
      group.items.forEach((item: any) => {
        assetData.push({ name: item.name, amount: item.amount });
      });
    });

    this.assetChartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Assets', color: 'white', fontSize: 13 },
      data: assetData,
      series: [
        {
          type: 'donut',
          angleKey: 'amount',
          calloutLabelKey: 'name',
          sectorLabelKey: 'amount',
          innerRadiusRatio: 0.7,
          strokeWidth: 0,
          calloutLabel: { enabled: false },
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params: any) => '$' + (params.value / 1000).toFixed(0) + 'k',
          },
          fills: ['#00a8ff', '#00d2ff', '#0072ff', '#48c6ef', '#6f86d6'],
        },
      ],
      legend: { enabled: false },
    } as any;

    // 3. Current Ratio Gauge (Donut with center label)
    this.currentRatio = this.totalLiabilities > 0 ? this.totalAssets / this.totalLiabilities : 0;
    
    this.gaugeChartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Current Ratio', color: 'white', fontSize: 13 },
      data: [
        { category: 'Used', value: Math.min(this.currentRatio, 3) },
        { category: 'Remaining', value: Math.max(0, 3 - this.currentRatio) }
      ],
      series: [
        {
          type: 'donut',
          angleKey: 'value',
          innerRadiusRatio: 0.8,
          strokeWidth: 0,
          fills: [this.currentRatio >= 1.5 ? '#00a8ff' : '#ff4d4d', 'rgba(255,255,255,0.05)'],
          calloutLabel: { enabled: false },
          sectorLabel: {
            enabled: true,
            color: 'white',
            fontWeight: 500,
            formatter: (params: any) => params.datum.category === 'Used' ? params.value.toFixed(1) : '',
          },
        }
      ],
      legend: { enabled: false },
    } as any;
  }
}
